import React, { useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { ACCESS, WASTE_LOCATIONS } from '../EstimateWizard.constants';
import { optionsFor } from './inventoryUtils';

const Label = ({ children }) => <div className="text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">{children}</div>;

const Pills = ({ options, value, onPick, testid, multi }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {options.map((o) => {
      const on = multi ? value.includes(o) : value === o;
      return (
        <button key={o} type="button" onClick={() => onPick(o)} className={`px-3 h-9 rounded-full border text-xs font-bold transition-colors ${on ? 'bg-[color:var(--mv-orange)] border-[color:var(--mv-orange)] text-white' : 'bg-white border-slate-200 text-[color:var(--mv-navy)] hover:border-slate-400'}`} data-testid={`${testid}-${o.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
          {multi && on ? '✓ ' : ''}{o}
        </button>
      );
    })}
  </div>
);

const Counter = ({ label, hint, qty, onChange, testid }) => (
  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-white">
    <div className="min-w-0">
      <div className="text-sm font-bold text-[color:var(--mv-navy)] leading-tight">{label}</div>
      {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <button type="button" disabled={!qty} onClick={() => onChange(Math.max(0, qty - 1))} className="w-9 h-9 rounded-md border-2 border-slate-300 inline-flex items-center justify-center disabled:opacity-40" data-testid={`${testid}-dec`}><Minus className="w-4 h-4" /></button>
      <span className="min-w-[1.75rem] text-center font-black text-[color:var(--mv-navy)]" data-testid={`${testid}-qty`}>{qty}</span>
      <button type="button" onClick={() => onChange(qty + 1)} className="w-9 h-9 rounded-md border-2 border-[color:var(--mv-orange)] bg-orange-50 text-[color:var(--mv-orange)] inline-flex items-center justify-center" data-testid={`${testid}-inc`}><Plus className="w-4 h-4" /></button>
    </div>
  </div>
);

const ItemList = ({ items, counts, onCount }) => {
  const [q, setQ] = useState('');
  const s = q.trim().toLowerCase();
  const shown = s ? items.filter(i => `${i.label} ${i.hint || ''} ${i.group || ''}`.toLowerCase().includes(s)) : items;
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="p-2 bg-slate-50 border-b border-slate-100">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()} placeholder="Search items" className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="inv-items-search" />
      </div>
      <div className="max-h-72 overflow-y-auto" data-testid="inv-items-list">
        {shown.length === 0 && <div className="p-3 text-sm text-slate-500">No match — add it in the notes.</div>}
        {shown.map((it, i) => (
          <React.Fragment key={it.id}>
            {it.group && it.group !== shown[i - 1]?.group && <div className="sticky top-0 z-[1] px-3 py-1 bg-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-600">{it.group}</div>}
            <Counter label={it.label} hint={it.hint} qty={counts[it.id] || 0} onChange={(n) => onCount(it.id, n)} testid={`inv-item-${it.id}`} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const BOXES = [['boxes_small', 'Small boxes', 'archive / shoebox'], ['boxes_medium', 'Medium boxes', 'standard removal box'], ['boxes_large', 'Large boxes', 'big / wardrobe box']];

export const JobInventoryFields = ({ service, value, onChange }) => {
  const { kind, items, sizes, bulky } = optionsFor(service);
  const set = (patch) => onChange({ ...value, ...patch });
  const clearance = kind === 'clearance';
  return (
    <div className="space-y-4" data-testid="inv-fields">
      <div><Label>{clearance ? 'How much waste?' : 'Property size'}</Label><Pills options={sizes} value={value.property_size} onPick={(o) => set({ property_size: value.property_size === o ? '' : o })} testid="inv-size" /></div>
      <div><Label>{clearance ? 'What kind of waste?' : kind === 'office' ? 'Office items' : 'Items moving'}</Label><ItemList items={items} counts={value.item_counts} onCount={(id, n) => set({ item_counts: { ...value.item_counts, [id]: n } })} /></div>
      {!clearance && (
        <div><Label>Boxes</Label>
          <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="inv-boxes">
            {BOXES.map(([k, l, h]) => <Counter key={k} label={l} hint={h} qty={value[k] || 0} onChange={(n) => set({ [k]: n })} testid={`inv-${k}`} />)}
          </div>
        </div>
      )}
      <div><Label>{clearance ? 'Special / hazardous items' : 'Extra-bulky items'}</Label><Pills multi options={bulky} value={value.extras} onPick={(o) => set({ extras: value.extras.includes(o) ? value.extras.filter(x => x !== o) : [...value.extras, o] })} testid="inv-extras" /></div>
      {clearance && <div><Label>Where is the rubbish?</Label><Pills options={WASTE_LOCATIONS} value={value.waste_location} onPick={(o) => set({ waste_location: o })} testid="inv-waste-loc" /></div>}
      <div><Label>{clearance ? 'Access to the rubbish' : 'Pickup access'}</Label><Pills options={ACCESS} value={value.pickup_access} onPick={(o) => set({ pickup_access: o })} testid="inv-pickup" /></div>
      {!clearance && <div><Label>Drop-off access</Label><Pills options={ACCESS} value={value.dropoff_access} onPick={(o) => set({ dropoff_access: o })} testid="inv-dropoff" /></div>}
      {!clearance && (
        <div className="flex flex-wrap gap-2">
          {[['packing', 'Packing service (+£25/hr)'], ['dismantle', 'Dismantle / reassemble']].map(([k, l]) => (
            <label key={k} className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-bold text-[color:var(--mv-navy)] cursor-pointer">
              <input type="checkbox" checked={!!value[k]} onChange={(e) => set({ [k]: e.target.checked })} data-testid={`inv-${k}`} /> {l}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
